import { colors } from '@kmns-internal/furikake'
import { Check, EditOutlined } from '@mui/icons-material'
import { IconButton, Stack, Tooltip, Typography } from '@mui/material'
import {
  ExcelConversionStepTypeEnum,
  Image,
  ResponseDatetime,
  ResponseDatetimeSubTypeEnum,
  ResponseEmployee,
  ResponseEmployeeSubTypeEnum,
  ResponseFormula,
  ResponseNumber,
  ResponseText,
  ResponseTextSubTypeEnum,
  ResponseTimeMeasurement,
  ResponseTypeEnum,
  ResponseURL,
  TemplateNodeSchema,
  TemplateQuestionExcelConversionTypes,
} from '@ulysses-inc/harami_api_client'
import { FC, ReactNode } from 'react'
import { useDispatch } from 'react-redux'
import { DEFAULT_DEVIATE_COMMENT } from 'src/features/templateEdit/common/types'
import { HoveringPosition } from 'src/features/templateEdit/dragAndDrop/DnDTemplateType'
import {
  changeActiveNodeId,
  updateTemplateNode,
} from 'src/state/ducks/templates/actions'
import { showEditHint } from 'src/state/ducks/templates/editHint/actions'
import { updateRepeatSerialNumber } from 'src/state/ducks/templates/editSerialNumber/actions'
import { useTemplateNodeNodes } from 'src/state/ducks/templates/selectors'
import ExcelLabelDropDown from '../itemAppender/ExcelLabelDropDown'
import ResponseDropDown from '../itemAppender/ResponseDropDown/ResponseDropDown'
import { QuestionProps } from './Question'
import {
  BasicTableCell,
  ColumnTexScoretWrapper,
  DraggingPlaceholder,
  Table,
} from './Question.components'
import { useDragAndDrop } from './Question.hooks'
import { TReactState, checkIsEmployeeCheck } from './Question.utils'
import QuestionNameAndButtons from './QuestionNameAndButtons'

export interface IQuestionBodyStates {
  isOpenState: TReactState<boolean>
  selectAnswerState: TReactState<ReactNode>
  hoverPositionState: TReactState<HoveringPosition>
}

const QuestionBody: FC<
  QuestionProps & ReturnType<typeof useDragAndDrop> & IQuestionBodyStates
> = ({
  node,
  isActive,
  parentNode,
  path,
  templateHints,
  templateNodes,
  isExcelConversion,
  isShowScore,
  isOpenState,
  selectAnswerState: [selectAnswer, setSelectAnswer],
  isOver,
  hoverPositionState: [hoverPosition],
  drag,
  preview,
}) => {
  const dispatch = useDispatch()
  const dispatchUpdateTemplateNode = (
    values: Partial<TemplateNodeSchema['question']>,
    nodeId: number = node.id,
  ) => dispatchUpdateSpecificTemplateNode(node, values, nodeId)
  const dispatchUpdateSpecificTemplateNode = (
    updateTarget: TemplateNodeSchema,
    values: Partial<TemplateNodeSchema['question']>,
    nodeId: number = node.id,
  ) =>
    dispatch(
      updateTemplateNode(nodeId, {
        ...updateTarget,
        question: { ...updateTarget.question, ...values },
      }),
    )
  const dispatchChangeActiveNodeId = () => dispatch(changeActiveNodeId(node.id))
  const dispatchShowEditHint = (hintId: string, blocks: unknown[]) =>
    dispatch(showEditHint(node, hintId, blocks))

  const templateNodeNodes = useTemplateNodeNodes(node)

  const excelConversionTypes = node.question?.excelConversionTypes ?? []
  const responseChoices = node.question?.responseMultipleChoices ?? []

  const hasSerialNumberTypes = [
    ExcelConversionStepTypeEnum.Multiple,
    ExcelConversionStepTypeEnum.Repeat,
  ]

  const onChangeQuestionType = (questionType: string) => {
    let responseType
    let responseNumbers: ResponseNumber[] = []
    let responseTexts: ResponseText[] = []
    let responseDatetimes: ResponseDatetime[] = []
    let responseTimeMeasurements: ResponseTimeMeasurement[] = []
    let responseInformations: Image[] = []
    let responseEmployees: ResponseEmployee[] = []
    let responseFormulas: ResponseFormula[] = []
    let responseURLs: ResponseURL[] = []

    switch (questionType) {
      case 'number':
        responseType = ResponseTypeEnum.NUMBER
        responseNumbers = []
        break
      case 'signature':
        responseType = ResponseTypeEnum.SIGNATURE
        break
      case 'text':
        responseType = ResponseTypeEnum.TEXT
        responseTexts = [{ subType: ResponseTextSubTypeEnum.SINGLE_LINE }]
        break
      case 'resultImage':
        responseType = ResponseTypeEnum.RESULT_IMAGE
        break
      case 'datetime':
        responseType = ResponseTypeEnum.DATETIME
        responseDatetimes = [{ subType: ResponseDatetimeSubTypeEnum.DATETIME }]
        break
      case 'timeMeasurement':
        responseType = ResponseTypeEnum.TIME_MEASUREMENT
        responseTimeMeasurements = []
        break
      case 'information':
        responseType = ResponseTypeEnum.INFORMATION
        responseInformations = []
        responseURLs = []
        break
      case 'employee':
        responseType = ResponseTypeEnum.EMPLOYEE
        responseEmployees = [
          { subType: ResponseEmployeeSubTypeEnum.NAME, isMultiple: 0 },
        ]
        break
      case 'formula':
        responseType = ResponseTypeEnum.FORMULA
        responseFormulas = []
        break
      case 'labelCheck':
        responseType = ResponseTypeEnum.LABEL_CHECK
        break
      default:
        return
    }

    dispatchUpdateTemplateNode({
      scale: '',
      isShowInformationImages: 0,
      informationImages: [],
      responseType,
      responseMultipleChoices: [],
      responseMultipleChoiceSets: [],
      responseNumbers: [...responseNumbers],
      responseTexts: [...responseTexts],
      responseDatetimes: [...responseDatetimes],
      responseTimeMeasurements: [...responseTimeMeasurements],
      responseInformations: [...responseInformations],
      responseEmployees: [...responseEmployees],
      responseFormulas: [...responseFormulas],
      responseGridVariables: [],
      responseLabelChecks: [],
      responseURLs: [...responseURLs],
      answerOption: undefined,
      deviateComment: DEFAULT_DEVIATE_COMMENT,
      deviateNotify: undefined,
    })
  }

  const onChangeSerialNumber = (changedIndex: number, value: number) => {
    const excelConversionTypes:
      | TemplateQuestionExcelConversionTypes[]
      | undefined
      | null = node.question?.excelConversionTypes

    if (!excelConversionTypes || !parentNode) {
      return
    }
    dispatchUpdateTemplateNode({
      excelConversionTypes: excelConversionTypes.map((c, index) => {
        if (index !== changedIndex) {
          return c
        }
        return { ...c, serialNumber: value }
      }),
    })
    if (
      excelConversionTypes[changedIndex]?.excelConversionType ===
      ExcelConversionStepTypeEnum.Repeat
    ) {
      dispatch(updateRepeatSerialNumber(parentNode.id, value))
      parentNode.nodes?.forEach(nodeId => {
        if (nodeId !== node.id) {
          const nd = templateNodes[nodeId]
          // 単に型の都合
          if (!nd) return
          dispatchUpdateSpecificTemplateNode(
            nd,
            {
              excelConversionTypes: nd?.question?.excelConversionTypes?.map(
                c =>
                  c.excelConversionType === ExcelConversionStepTypeEnum.Repeat
                    ? { ...c, serialNumber: value }
                    : c,
              ),
            },
            nodeId,
          )
        }
      })
    }
  }
  const onClickLabel = (stepType: number, checked: boolean) => {
    const excelConversionTypes:
      | TemplateQuestionExcelConversionTypes[]
      | undefined
      | null = node.question?.excelConversionTypes
    if (!excelConversionTypes) {
      if (checked) {
        dispatchUpdateTemplateNode({
          excelConversionTypes: [
            {
              excelConversionType: stepType,
              serialNumber: hasSerialNumberTypes.includes(stepType)
                ? 1
                : undefined,
            },
          ],
        })
      }
      return
    }
    const isContainType =
      excelConversionTypes.find(c => c.excelConversionType === stepType) ??
      false
    if (!isContainType && checked) {
      dispatchUpdateTemplateNode({
        excelConversionTypes: [
          ...excelConversionTypes,
          {
            excelConversionType: stepType,
            serialNumber: hasSerialNumberTypes.includes(stepType)
              ? 1
              : undefined,
          },
        ],
      })
      return
    }
    if (isContainType && !checked) {
      dispatchUpdateTemplateNode({
        excelConversionTypes: excelConversionTypes.filter(
          c => c.excelConversionType !== stepType,
        ),
      })
      return
    }
  }

  const score = () => {
    return isMultipleChoice() && responseChoices.length
      ? responseChoices[0]?.responses
          .filter(m => !m.isExcludedFromScoring)
          .map(({ score }) => score)
          .reduce((prev, cur) => Math.max(prev, cur), 0)
      : '/'
  }

  const isMultipleChoice = () =>
    node.question?.responseType === ResponseTypeEnum.MULTIPLE_CHOICE
  const isEmployeeCheck = checkIsEmployeeCheck(parentNode, node)

  return (
    <>
      {isOver && hoverPosition === 'TOP' && <DraggingPlaceholder />}
      <Table onClick={dispatchChangeActiveNodeId} data-testid="question-body">
        <QuestionNameAndButtons
          {...{
            node,
            parentNode,
            isActive,
            templateNodes,
            isOpenState,
            preview,
            drag,
          }}
        />
        <ResponseDropDown
          hasLogic={!!templateNodeNodes.length}
          selectAnswer={selectAnswer}
          setSelectAnswer={setSelectAnswer}
          onChangeQuestionType={onChangeQuestionType}
          dispatchUpdateTemplateNode={dispatchUpdateTemplateNode}
          isEmployeeCheck={isEmployeeCheck}
        />
        {templateHints.map(({ id }) => {
          const questionHint = node.question?.hints?.filter(
            ({ hintId }) => hintId === id,
          )[0]

          return (
            <BasicTableCell key={id} theme={{ minWidth: '200px' }}>
              {!isEmployeeCheck && (
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Tooltip title="ヒントを編集">
                    <IconButton
                      onClick={() =>
                        dispatchShowEditHint(
                          id ?? '',
                          questionHint?.blocks ?? [],
                        )
                      }
                      data-testid="edit-hint-button"
                      aria-label="ヒントを編集"
                      color="primary"
                    >
                      <EditOutlined color="primary" sx={{ fontSize: 20 }} />
                    </IconButton>
                  </Tooltip>
                  {!!(questionHint?.blocks ?? []).length && (
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={0.5}
                      sx={{ mr: 2 }}
                    >
                      <Check sx={{ color: colors.success.main }} />
                      <Typography
                        variant="body2"
                        sx={{ color: colors.success.main }}
                      >
                        入力済
                      </Typography>
                    </Stack>
                  )}
                </Stack>
              )}
            </BasicTableCell>
          )
        })}
        {isExcelConversion && (
          <ExcelLabelDropDown
            checkedExcelConversionTypes={excelConversionTypes}
            onClickLabel={onClickLabel}
            isRepeat={
              path.find(
                parentId => templateNodes[parentId]?.section?.isRepeat,
              ) !== undefined
            }
            onChangeSerialNumber={onChangeSerialNumber}
          />
        )}
        {isShowScore && (
          <BasicTableCell
            theme={{
              minWidth: '90px',
              backgroundColor:
                !responseChoices.length && colors.action.disabledBackground,
            }}
          >
            <ColumnTexScoretWrapper
              theme={{
                color: !responseChoices.length
                  ? colors.text.disabled
                  : colors.text.primary,
              }}
            >
              {score()}
            </ColumnTexScoretWrapper>
          </BasicTableCell>
        )}
      </Table>
      {isOver && hoverPosition === 'BOTTOM' && <DraggingPlaceholder />}
    </>
  )
}

export default QuestionBody
